import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    preference: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'message') {
      setCharCount(value.length);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        preference: formData.preference,
        message: formData.message,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d50aivnjl0d25saag590', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          preference: '',
          message: '',
        });
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 px-2">
            Get in touch with us
          </h2>
          <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
          <div className="flex items-center justify-center gap-3 mb-2">
            <img
              src="https://static.wixstatic.com/media/288b4c_cdd9acebf50e495592734f144fbf4ac7~mv2.png/v1/fill/w_54,h_31,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/288b4c_cdd9acebf50e495592734f144fbf4ac7~mv2.png"
              alt="Renoboys logo"
              className="h-10 w-auto"
            />
          </div>
          <p className="text-sm sm:text-base text-gray-600 font-medium mb-6">
            Quality you can see. Reno's you can trust.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
          <div className="space-y-5 sm:space-y-6">
            <div className="bg-white p-5 sm:p-6 rounded-xl shadow-md">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-xl text-orange-500"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-base sm:text-lg text-gray-900 mb-1">Phone</h3>
                  <a
                    href="tel:021 086 21239"
                    className="text-sm sm:text-base text-gray-600 hover:text-orange-500 transition-colors cursor-pointer"
                  >
                    021 086 21239
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white p-5 sm:p-6 rounded-xl shadow-md">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-xl text-orange-500"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-base sm:text-lg text-gray-900 mb-1">Email</h3>
                  <a
                    href="mailto:info@renoboys.co.nz"
                    className="text-sm sm:text-base text-gray-600 hover:text-orange-500 transition-colors cursor-pointer break-all"
                  >
                    info@renoboys.co.nz
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white p-5 sm:p-6 rounded-xl shadow-md">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-xl text-orange-500"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-base sm:text-lg text-gray-900 mb-1">Location</h3>
                  <p className="text-sm sm:text-base text-gray-600">Kingsland</p>
                  <p className="text-sm sm:text-base text-gray-600">Servicing Auckland region wide</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg">
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="space-y-4 sm:space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Phone number"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="preference" className="block text-sm font-medium text-gray-700 mb-2">
                    Prefer Phone or Email? <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="preference"
                    name="preference"
                    value={formData.preference}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select preference</option>
                    <option value="Phone">Phone</option>
                    <option value="Email">Email</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    How can we help? <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="Tell us about your project..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none text-sm"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{charCount}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || formData.message.length > 500}
                  className="w-full bg-orange-500 text-white py-3.5 px-6 rounded-lg font-semibold hover:bg-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-base"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800 font-medium">
                      Thank you! Your message has been sent successfully.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800 font-medium">
                      Something went wrong. Please try again or contact us directly.
                    </p>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}